/**
 * @fileoverview Own validated browser session identity for protected workspaces.
 *
 * `/api/validate` on load, and login/onboarding responses through `applyAuthState`,
 * supply authentication, onboarding, admin, and employee context from
 * `kc-internal-backend/functions/auth.py`. The session itself is an httpOnly cookie;
 * this state mirrors it, and `setIsAuthenticated` does not create a server session.
 * Any identity change clears document snapshots through
 * `store/Documents/dataApi.js#clearDocumentSession` so cached data cannot cross accounts.
 *
 * `AppAccessContext.js` owns workspace availability; `App.js` owns route admission.
 * Resource and administrator permissions remain backend responsibilities.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { ReactReduxContext } from 'react-redux'
import { clearDocumentSession } from './store/Documents/dataApi'
import api from './api'

export const AuthContext = createContext()

const DEFAULT_AUTH_STATE = {
  isAuthenticated: false,
  requiresOnboarding: false,
  employeeId: null,
  isAdmin: false,
  onboardingCompletedAt: null,
  onboardingBlockedReason: null,
}



function normalizeAuthState(payload = {}) {
  return {
    isAuthenticated: payload.valid === true || payload.isAuthenticated === true,
    requiresOnboarding: payload.requiresOnboarding === true,
    employeeId: payload.employeeId ?? null,
    isAdmin: payload.isAdmin === true,
    onboardingCompletedAt: payload.onboardingCompletedAt ?? null,
    onboardingBlockedReason: payload.onboardingBlockedReason ?? null,
  }
}



export function AuthProvider({ children }) {
  const reduxContext = useContext(ReactReduxContext)
  const identity = useRef(null)
  const resetDocumentIdentity = useCallback(

  (next) => {
    if (identity.current !== next && reduxContext?.store) clearDocumentSession(reduxContext.store)
    identity.current = next
  }, [reduxContext])
  const [authState, setAuthState] = useState(DEFAULT_AUTH_STATE)
  const [isLoading, setIsLoading] = useState(true)

  const applyAuthState = useCallback(

  (payload = {}) => {
    const next = normalizeAuthState(payload)
    resetDocumentIdentity(next.isAuthenticated ? String(next.employeeId) : null)
    setAuthState(next)
  }, [resetDocumentIdentity])

  const clearAuthState = useCallback(

  () => {
    resetDocumentIdentity(null)
    setAuthState({ ...DEFAULT_AUTH_STATE })
  }, [resetDocumentIdentity])

  const setIsAuthenticated = useCallback(

  (value) => {
    if (!value) resetDocumentIdentity(null)
    setAuthState((previous) => (
      value
        ? { ...previous, isAuthenticated: true }
        : { ...DEFAULT_AUTH_STATE }
    ))
  }, [resetDocumentIdentity])

  const validateToken = useCallback(

  async () => {
    try {
      const response = await api.get('/validate')
      applyAuthState(response.data)
    } catch (error) {
      console.error(error)
      clearAuthState()
    } finally {
      setIsLoading(false)
    }
  }, [applyAuthState, clearAuthState])

  useEffect(() => {
    validateToken()
  }, [validateToken])

  const contextValue = useMemo(() => ({
    ...authState,
    setIsAuthenticated,
    applyAuthState,
    clearAuthState,
    validateToken,
  }), [authState, setIsAuthenticated, applyAuthState, clearAuthState, validateToken])

  if (isLoading) {
    return <div>Loading...</div>
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  )
}
